import React, { useState } from "react";
import { Card, Tabs, Button, Space, Table, Tag, Modal, Form, Input, DatePicker, InputNumber, Select, message, Switch, Divider, Row, Col } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, PercentageOutlined, CalendarOutlined, FileTextOutlined, UserOutlined } from "@ant-design/icons";
import { useTable } from "@refinedev/antd";
import { useCreate, useUpdate, useDelete } from "@refinedev/core";
import dayjs from "dayjs";

const { TabPane } = Tabs;
const { TextArea } = Input;

interface IPriceProfile {
  uid?: number;
  name?: string;
  title?: string;
  description?: string;
  start_date_pricing?: string;
  end_date_pricing?: string;
  price: any;
  status?: boolean;
  type?: string;
  vat?: number;
  optional?: boolean;
  per_day?: boolean;
  per_pers_day?: boolean;
  add_to_booking_price?: boolean;
  priority?: number;
}

export const PriceSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('standard');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();

  const { mutate: createPrice } = useCreate();
  const { mutate: updatePrice } = useUpdate();
  const { mutate: deletePrice } = useDelete();

  // Table configurations for different price types
  const { tableProps: standardPriceProps } = useTable({
    resource: 'standardprices',
    syncWithLocation: false,
  });

  const { tableProps: taxSettingsProps } = useTable({
    resource: 'taxsettings',
    syncWithLocation: false,
  });

  const { tableProps: additionalPriceProps } = useTable({
    resource: 'additionalprices',
    syncWithLocation: false,
  });

  const { tableProps: pricePeriodProps } = useTable({
    resource: 'priceperiods',
    syncWithLocation: false,
  });

  const handleCreate = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingItem(record);
    
    // Map fields based on resource type
    let formValues = { ...record };
    
    if (activeTab === 'standard' || activeTab === 'taxes') {
      formValues.dateRange = record.start_date_pricing && record.end_date_pricing ? 
        [dayjs(record.start_date_pricing), dayjs(record.end_date_pricing)] : undefined;
    } else if (activeTab === 'periods') {
      formValues.dateRange = record.start_date_pricing && record.end_date_pricing ? 
        [dayjs(record.start_date_pricing), dayjs(record.end_date_pricing)] : undefined;
    }
    
    if (activeTab === 'standard' || activeTab === 'periods') {
      // Parse price JSON if it's a string for standard prices and periods
      if (typeof record.price === 'string') {
        const parsedPrice = JSON.parse(record.price);
        formValues = {
          ...formValues,
          ...parsedPrice
        };
      }
    }
    
    if (activeTab === 'taxes') {
      // Parse price JSON if it's a string
      if (typeof record.price === 'string') {
        formValues.price = JSON.parse(record.price);
      }
    }
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Sind Sie sicher?',
      content: 'Möchten Sie dieses Preisprofil wirklich löschen?',
      okText: 'Ja, löschen',
      cancelText: 'Abbrechen',
      okType: 'danger',
      onOk: () => {
        deletePrice({
          resource: getResourceByTab(),
          id: id,
        }, {
          onSuccess: () => {
            message.success('Preisprofil erfolgreich gelöscht');
          },
          onError: () => {
            message.error('Fehler beim Löschen des Preisprofils');
          }
        });
      }
    });
  };

  const getResourceByTab = () => {
    switch (activeTab) {
      case 'standard': return 'standardprices';
      case 'taxes': return 'taxsettings';
      case 'additional': return 'additionalprices';
      case 'periods': return 'priceperiods';
      default: return 'standardprices';
    }
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      let data = { ...values };
      
      // Handle date fields based on resource type
      if (activeTab === 'standard' || activeTab === 'taxes') {
        data = {
          ...values,
          start_date_pricing: values.dateRange?.[0]?.format('YYYY-MM-DD'),
          end_date_pricing: values.dateRange?.[1]?.format('YYYY-MM-DD'),
          dateRange: undefined,
        };
      } else if (activeTab === 'periods') {
        data = {
          ...values,
          start_date_pricing: values.dateRange?.[0]?.format('YYYY-MM-DD'),
          end_date_pricing: values.dateRange?.[1]?.format('YYYY-MM-DD'),
          dateRange: undefined,
        };
      }
      
      if (activeTab === 'standard' || activeTab === 'periods') {
        // Create price object for standard prices and periods
        const priceData = {
          forOnePerson: values.forOnePerson || 0,
          forTwoPersons: values.forTwoPersons || 0,
          perAdditionalPerson: values.perAdditionalPerson || 0,
          perDog: values.perDog || 0,
          perChildAgeFrom: values.perChildAgeFrom || 0,
          perChildAgeTo: values.perChildAgeTo || 0,
          perChild: values.perChild || 0,
          perChildAgeFromB: values.perChildAgeFromB || 0,
          perChildAgeToB: values.perChildAgeToB || 0,
          perChildB: values.perChildB || 0,
        };
        
        // Clean up individual price fields from data
        delete data.forOnePerson;
        delete data.forTwoPersons;
        delete data.perAdditionalPerson;
        delete data.perDog;
        delete data.perChildAgeFrom;
        delete data.perChildAgeTo;
        delete data.perChild;
        delete data.perChildAgeFromB;
        delete data.perChildAgeToB;
        delete data.perChildB;
        
        data.price = JSON.stringify(priceData);
      }
      
      if (activeTab === 'taxes') {
        // Ensure price is JSON string
        if (data.price && typeof data.price === 'object') {
          data.price = JSON.stringify(data.price);
        }
      }

      if (editingItem) {
        updatePrice({
          resource: getResourceByTab(),
          id: editingItem.uid,
          values: data,
        }, {
          onSuccess: () => {
            message.success('Preisprofil erfolgreich aktualisiert');
            setModalVisible(false);
            form.resetFields();
          },
          onError: () => {
            message.error('Fehler beim Aktualisieren des Preisprofils');
          }
        });
      } else {
        createPrice({
          resource: getResourceByTab(),
          values: data,
        }, {
          onSuccess: () => {
            message.success('Preisprofil erfolgreich erstellt');
            setModalVisible(false);
            form.resetFields();
          },
          onError: () => {
            message.error('Fehler beim Erstellen des Preisprofils');
          }
        });
      }
    });
  };

  // Column definitions for standard prices
  const standardPriceColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Beschreibung',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Preis (1-2 Pers.)',
      key: 'price_1_2',
      width: 120,
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        return (
          <Space>
            <span>{price?.forOnePerson || 0}€</span>
            <span>/</span>
            <span>{price?.forTwoPersons || 0}€</span>
          </Space>
        );
      },
    },
    {
      title: 'Pro weitere',
      key: 'price_additional',
      width: 100,
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        return `${price?.perAdditionalPerson || 0}€`;
      },
    },
    {
      title: 'Kind / Hund',
      key: 'price_child_dog',
      width: 100,
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        return (
          <Space>
            <span>{price?.perChild || 0}€</span>
            <span>/</span>
            <span>{price?.perDog || 0}€</span>
          </Space>
        );
      },
    },
    {
      title: 'Gültig von',
      dataIndex: 'start_date_pricing',
      key: 'start_date_pricing',
      width: 100,
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Gültig bis',
      dataIndex: 'end_date_pricing',
      key: 'end_date_pricing',
      width: 100,
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: boolean) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.uid)} />
        </Space>
      ),
    },
  ];

  // Column definitions for tax settings
  const taxColumns = [
    {
      title: 'Bezeichnung',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Typ',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        let label = 'Pauschal';
        let icon = <DollarOutlined />;
        if (type === 'percent') {
          label = 'Prozentual';
          icon = <PercentageOutlined />;
        } else if (type === 'perperson') {
          label = 'Pro Person';
          icon = <UserOutlined />;
        }
        return <Tag icon={icon}>{label}</Tag>;
      },
    },
    {
      title: 'Wert',
      key: 'value',
      render: (_: any, record: any) => {
        const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
        if (record.type === 'percent') {
          return `${price?.pauschal || 0}%`;
        } else if (record.type === 'perperson') {
          return `${price?.forOnePerson || 0}€ pro Person`;
        } else {
          return `${price?.pauschal || 0}€`;
        }
      },
    },
    {
      title: 'Gültig von',
      dataIndex: 'start_date_pricing',
      key: 'start_date_pricing',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Gültig bis',
      dataIndex: 'end_date_pricing',
      key: 'end_date_pricing',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.uid)} />
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Preiseinstellungen - Vorlagen verwalten"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Neu erstellen
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <DollarOutlined />
              Standardpreise
            </span>
          }
          key="standard"
        >
          <Table
            {...standardPriceProps}
            columns={standardPriceColumns}
            rowKey="uid"
            scroll={{ x: 800 }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <PercentageOutlined />
              Steuern & Gebühren
            </span>
          }
          key="taxes"
        >
          <Table
            {...taxSettingsProps}
            columns={taxColumns}
            rowKey="uid"
            scroll={{ x: 600 }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <CalendarOutlined />
              Zeitperioden
            </span>
          }
          key="periods"
        >
          <Table
            {...pricePeriodProps}
            columns={[
              {
                title: 'Bezeichnung',
                dataIndex: 'title',
                key: 'title',
              },
              {
                title: 'Preis (1-2 Pers.)',
                key: 'price_1_2',
                render: (_: any, record: any) => {
                  const price = typeof record.price === 'string' ? JSON.parse(record.price) : record.price;
                  return (
                    <Space>
                      <span>{price?.forOnePerson || 0}€</span>
                      <span>/</span>
                      <span>{price?.forTwoPersons || 0}€</span>
                    </Space>
                  );
                },
              },
              {
                title: 'Gültig von',
                dataIndex: 'start_date_pricing',
                key: 'start_date_pricing',
                render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
              },
              {
                title: 'Gültig bis',
                dataIndex: 'end_date_pricing',
                key: 'end_date_pricing',
                render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
              },
              {
                title: 'Priorität',
                dataIndex: 'priority',
                key: 'priority',
                render: (priority: number) => (
                  <Tag color={priority > 5 ? 'red' : priority > 0 ? 'orange' : 'default'}>
                    {priority || 0}
                  </Tag>
                ),
              },
              {
                title: 'Aktionen',
                key: 'actions',
                render: (_: any, record: any) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.uid)} />
                  </Space>
                ),
              },
            ]}
            rowKey="uid"
            scroll={{ x: 700 }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <FileTextOutlined />
              Zusatzleistungen
            </span>
          }
          key="additional"
        >
          <Table
            {...additionalPriceProps}
            columns={[
              {
                title: 'Bezeichnung',
                dataIndex: 'title',
                key: 'title',
              },
              {
                title: 'Preis',
                dataIndex: 'price',
                key: 'price',
                render: (price: number) => `${price} €`,
              },
              {
                title: 'MwSt.',
                dataIndex: 'vat',
                key: 'vat',
                render: (vat: number) => `${vat}%`,
              },
              {
                title: 'Optional',
                dataIndex: 'optional',
                key: 'optional',
                render: (optional: boolean) => (
                  <Tag color={optional ? 'blue' : 'green'}>
                    {optional ? 'Optional' : 'Pflicht'}
                  </Tag>
                ),
              },
              {
                title: 'Aktionen',
                key: 'actions',
                render: (_: any, record: any) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.uid)} />
                  </Space>
                ),
              },
            ]}
            rowKey="uid"
            scroll={{ x: 600 }}
          />
        </TabPane>
      </Tabs>

      {/* Vollständiges Modal für Create/Edit */}
      <Modal
        title={editingItem ? 'Preisprofil bearbeiten' : 'Neues Preisprofil'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {activeTab === 'standard' && (
            <>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Bitte geben Sie einen Namen ein' }]}
              >
                <Input placeholder="z.B. Hauptsaison 2024" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Beschreibung"
              >
                <TextArea rows={3} placeholder="Optionale Beschreibung..." />
              </Form.Item>

              <Form.Item
                name="dateRange"
                label="Gültigkeitszeitraum"
                getValueFromEvent={(dates) => dates && dates.length === 2 ? [dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] : undefined}
                getValueProps={(value) => ({ 
                  value: value && value.length === 2 ? [dayjs(value[0]), dayjs(value[1])] : undefined 
                })}
              >
                <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                valuePropName="checked"
                initialValue={true}
              >
                <Select>
                  <Select.Option value={true}>Aktiv</Select.Option>
                  <Select.Option value={false}>Inaktiv</Select.Option>
                </Select>
              </Form.Item>

              <Divider>Preise</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="forOnePerson"
                    label="Preis für 1 Person"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="forTwoPersons"
                    label="Preis für 2 Personen"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="perAdditionalPerson"
                label="Preis pro zusätzliche Person"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  addonBefore="€"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="perChild"
                    label="Preis pro Kind"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="perDog"
                    label="Preis pro Hund"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {activeTab === 'taxes' && (
            <>
              <Form.Item
                name="title"
                label="Bezeichnung"
                rules={[{ required: true, message: 'Bitte geben Sie eine Bezeichnung ein' }]}
              >
                <Input placeholder="z.B. Kurtaxe" />
              </Form.Item>

              <Form.Item
                name="dateRange"
                label="Gültigkeitszeitraum"
                getValueFromEvent={(dates) => dates && dates.length === 2 ? [dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] : undefined}
                getValueProps={(value) => ({ 
                  value: value && value.length === 2 ? [dayjs(value[0]), dayjs(value[1])] : undefined 
                })}
              >
                <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="type"
                label="Typ"
                rules={[{ required: true, message: 'Bitte wählen Sie einen Typ' }]}
              >
                <Select>
                  <Select.Option value="percent">Prozentual</Select.Option>
                  <Select.Option value="fixed">Pauschal</Select.Option>
                  <Select.Option value="perperson">Pro Person</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
              >
                {({ getFieldValue }) => {
                  const type = getFieldValue('type');
                  
                  if (type === 'fixed' || type === 'percent') {
                    return (
                      <Form.Item
                        name={['price', 'pauschal']}
                        label="Wert"
                        rules={[{ required: true, message: 'Bitte geben Sie einen Wert ein' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          precision={2}
                          addonAfter={type === 'percent' ? '%' : '€'}
                        />
                      </Form.Item>
                    );
                  } else if (type === 'perperson') {
                    return (
                      <Form.Item
                        name={['price', 'forOnePerson']}
                        label="Preis pro Erwachsener"
                        rules={[{ required: true, message: 'Bitte geben Sie einen Preis ein' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          precision={2}
                          addonBefore="€"
                        />
                      </Form.Item>
                    );
                  }
                  
                  return null;
                }}
              </Form.Item>
            </>
          )}

          {activeTab === 'periods' && (
            <>
              <Form.Item
                name="title"
                label="Bezeichnung"
                rules={[{ required: true, message: 'Bitte geben Sie eine Bezeichnung ein' }]}
              >
                <Input placeholder="z.B. Hauptsaison 2024" />
              </Form.Item>

              <Form.Item
                name="dateRange"
                label="Gültigkeitszeitraum"
                rules={[{ required: true, message: 'Bitte wählen Sie einen Zeitraum' }]}
                getValueFromEvent={(dates) => dates && dates.length === 2 ? [dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] : undefined}
                getValueProps={(value) => ({ 
                  value: value && value.length === 2 ? [dayjs(value[0]), dayjs(value[1])] : undefined 
                })}
              >
                <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="priority"
                label="Priorität"
                initialValue={0}
                extra="Höhere Priorität überschreibt niedrigere bei Überschneidungen"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                />
              </Form.Item>

              <Divider>Preise für diese Zeitperiode</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="forOnePerson"
                    label="Preis für 1 Person"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="forTwoPersons"
                    label="Preis für 2 Personen"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {activeTab === 'additional' && (
            <>
              <Form.Item
                name="title"
                label="Bezeichnung"
                rules={[{ required: true, message: 'Bitte geben Sie eine Bezeichnung ein' }]}
              >
                <Input placeholder="z.B. Bettwäsche-Set" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Beschreibung"
              >
                <TextArea rows={3} placeholder="Detaillierte Beschreibung der Zusatzleistung..." />
              </Form.Item>

              <Form.Item
                name="price"
                label="Preis"
                rules={[{ required: true, message: 'Bitte geben Sie einen Preis ein' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  addonBefore="€"
                />
              </Form.Item>

              <Form.Item
                name="vat"
                label="MwSt. %"
                initialValue={19}
              >
                <Select>
                  <Select.Option value={0}>0% (steuerfrei)</Select.Option>
                  <Select.Option value={7}>7% (ermäßigt)</Select.Option>
                  <Select.Option value={19}>19% (normal)</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="optional"
                label="Typ"
                initialValue={true}
              >
                <Select>
                  <Select.Option value={true}>Optional (Gast kann wählen)</Select.Option>
                  <Select.Option value={false}>Pflicht (automatisch hinzugefügt)</Select.Option>
                </Select>
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="per_day"
                    label="Berechnung"
                    initialValue={false}
                  >
                    <Select>
                      <Select.Option value={false}>Einmalig</Select.Option>
                      <Select.Option value={true}>Pro Tag</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="per_pers_day"
                    label="Pro Person berechnen"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch checkedChildren="Ja" unCheckedChildren="Nein" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="add_to_booking_price"
                label="Zum Gesamtpreis hinzufügen"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Ja" unCheckedChildren="Nein" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Card>
  );
}; 